import React from 'react';
import { Moon, Star, Sparkles, Sun, Pencil, Backpack, Ruler, GraduationCap, Percent, Gift, ShoppingBag, PartyPopper, Umbrella, Waves, Tag, Zap } from 'lucide-react';

const Float = ({ Icon, x, y, size = 28, delay = 0, dur = 7, spin = false, color, opacity = 0.9, anim = 'live-float' }) => (
  <span className={`absolute ${anim}`} style={{ left: x, top: y, animationDelay: `${delay}s`, animationDuration: `${dur}s`, color, opacity }}>
    <Icon size={size} strokeWidth={1.5} className={spin ? 'live-spin' : ''} />
  </span>
);

const Stars = ({ n, color, seed = 1 }) => (
  <>{Array.from({ length: n }).map((_, i) => { const r = (i * 9301 + seed * 49297) % 233280 / 233280; return (
    <span key={i} className="absolute rounded-full live-twinkle" style={{ left: `${(r * 100).toFixed(1)}%`, top: `${((r * 7919) % 1 * 100).toFixed(1)}%`, width: 2 + (i % 3), height: 2 + (i % 3), background: color, animationDelay: `${(i % 7) * 0.6}s`, animationDuration: `${2.5 + (i % 4)}s` }} />
  ); })}</>
);

const Fireworks = ({ color, color2 }) => (
  <>{[['12%', '18%', 0], ['78%', '12%', 1.4], ['55%', '60%', 2.8], ['90%', '55%', 4.2]].map(([x, y, d], i) => (
    <span key={i} className="absolute rounded-full live-burst" style={{ left: x, top: y, width: 70, height: 70, marginLeft: -35, marginTop: -35, borderColor: i % 2 ? color2 : color, animationDelay: `${d}s` }} />
  ))}</>
);

const Lanterns = ({ color }) => (
  <>{[['82%', 0], ['90%', 1.6], ['74%', 3.1]].map(([x, d], i) => (
    <span key={i} className="absolute live-rise" style={{ left: x, bottom: '-20%', animationDelay: `${d}s`, animationDuration: `${11 + i * 2}s` }}>
      <span className="block w-px h-6 mx-auto" style={{ background: color, opacity: 0.5 }} />
      <span className="block w-4 h-6 rounded-[40%] shadow-[0_0_18px_rgba(217,178,90,.7)]" style={{ background: `linear-gradient(${color}, #8a6a1f)` }} />
      <span className="block w-1.5 h-2 mx-auto rounded-b-full" style={{ background: color }} />
    </span>
  ))}</>
);

const SCENES = {
  ramadan: (a) => (<>
    <Stars n={26} color={a} seed={3} />
    <span className="absolute -top-6 end-8 live-glow" style={{ color: a }}><Moon size={150} strokeWidth={0.8} className="rotate-[-25deg]" fill="currentColor" fillOpacity={0.9} /></span>
    <Lanterns color={a} />
    <span className="absolute bottom-0 inset-x-0 h-16 live-arches" style={{ '--c': a }} />
  </>),
  dsf: (a) => (<>
    <Stars n={20} color={a} seed={5} />
    <Fireworks color={a} color2="#ff5a5f" />
    <Float Icon={Gift} x="70%" y="18%" size={30} delay={0} color={a} />
    <Float Icon={ShoppingBag} x="84%" y="55%" size={34} delay={1.2} color="#ffffff" opacity={0.75} />
    <Float Icon={Sparkles} x="60%" y="62%" size={22} delay={2} color={a} />
    <Float Icon={PartyPopper} x="92%" y="20%" size={26} delay={0.6} color="#ff5a5f" />
  </>),
  dss: (a) => (<>
    <span className="absolute -top-16 end-6 live-spin-slow" style={{ color: a, opacity: 0.85 }}><Sun size={190} strokeWidth={0.9} /></span>
    <Float Icon={Umbrella} x="66%" y="50%" size={34} delay={0.4} color="#ffffff" opacity={0.85} />
    <Float Icon={Waves} x="80%" y="72%" size={40} delay={1.1} dur={5} color={a} opacity={0.7} anim="live-sway" />
    <span className="absolute bottom-0 inset-x-0 h-10 live-waves" style={{ '--c': a }} />
  </>),
  school: (a) => (<>
    <span className="absolute inset-0 live-grid" />
    <Float Icon={Backpack} x="68%" y="12%" size={44} delay={0} color={a} />
    <Float Icon={Pencil} x="82%" y="58%" size={36} delay={1.3} color="#ffffff" opacity={0.85} />
    <Float Icon={Ruler} x="90%" y="18%" size={32} delay={0.7} color="#7fb2ff" />
    <Float Icon={GraduationCap} x="58%" y="66%" size={34} delay={2.1} color={a} opacity={0.8} />
  </>),
  national: (a) => (<>
    <span className="absolute inset-y-0 end-0 w-1/3 live-flag" />
    <Stars n={14} color={a} seed={9} />
    <span className="absolute top-6 end-10 live-glow" style={{ color: a }}><Star size={90} strokeWidth={1} fill="currentColor" fillOpacity={0.35} /></span>
    <Float Icon={Sparkles} x="64%" y="60%" size={26} delay={1} color="#ffffff" opacity={0.8} />
  </>),
  friday: (a) => (<>
    <span className="absolute inset-0 live-scan" />
    <Float Icon={Percent} x="72%" y="10%" size={60} delay={0} color={a} opacity={0.9} anim="live-drop" />
    <Float Icon={Tag} x="86%" y="50%" size={36} delay={1.5} color="#E50914" anim="live-drop" />
    <Float Icon={Zap} x="60%" y="55%" size={30} delay={0.8} color={a} opacity={0.7} anim="live-drop" />
    <Float Icon={Percent} x="93%" y="20%" size={28} delay={2.4} color="#ffffff" opacity={0.5} anim="live-drop" />
  </>),
};

const LiveScene = ({ theme, accent, compact = false }) => {
  const render = SCENES[theme] || SCENES.dsf;
  return <div className={`absolute inset-y-0 start-0 overflow-hidden pointer-events-none ${compact ? 'live-scene-compact end-[34%] md:end-[38%]' : 'end-0'}`} aria-hidden="true" data-testid="live-scene">{render(accent)}</div>;
};

export default LiveScene;
